import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { baseApiURL } from "../../../baseUrl";
import { FiSearch, FiUpload, FiX } from "react-icons/fi";
import { getFileUrl } from "../../../utils/fileUrl";

const EditStudent = () => {
  const [file, setFile] = useState();
  const [branch, setBranch] = useState([]);
  const [search, setSearch] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [data, setData] = useState({
    enrollmentNo: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    FatherName: "",
    MotherName: "",
    FatherPhoneNumber: "",
    MotherPhoneNumber: "",
    semester: "",
    branch: "",
    batch: "",
    regulation: "",
    gender: "",
    profile: "",
    detained: false,
    passed: false,
  });
  const [id, setId] = useState("");

  const getBranchData = () => {
    const headers = {
      "Content-Type": "application/json",
    };
    axios
      .get(`${baseApiURL()}/branch/getBranch`, { headers })
      .then((response) => {
        if (response.data.success) {
          setBranch(response.data.branches);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load branches");
      });
  };

  useEffect(() => {
    getBranchData();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    const imageUrl = URL.createObjectURL(selectedFile);
    setPreviewImage(imageUrl);
  };

  const handleDetainStudent = async () => {
    try {
      const loadingToast = toast.loading("Moving student to detained records...");
      
      const response = await axios.post(
        `${baseApiURL()}/student/detain/detain`,
        { 
          studentId: id,
          detentionReason: "Student detained via edit form"
        },
        {
          headers: {
            "Content-Type": "application/json",
          }
        }
      );

      toast.dismiss(loadingToast);
      if (response.data.success) {
        toast.success("Student moved to detained records successfully");
        clearSearchHandler();
      } else {
        toast.error(response.data.message || "Failed to detain student");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.message || "Error detaining student");
      console.error("Detain error:", error);
    }
  };

  const updateStudentProfile = async (e) => {
    e.preventDefault();
    
    // Only validate essential fields
    if (!data.firstName || !data.lastName || !data.phoneNumber || !data.enrollmentNo) {
      toast.error("First Name, Last Name, Phone Number, and Enrollment No are required");
      return;
    }

    // Check if detained checkbox was checked
    if (data.detained) {
      // Show confirmation dialog
      toast((t) => (
        <div className="text-center">
          <p className="mb-4">This will move the student to detained records and remove them from active students. Continue?</p>
          <div className="flex justify-center gap-4">
            <button 
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              onClick={async () => {
                toast.dismiss(t.id);
                await handleDetainStudent();
              }}
            >
              Yes, Detain Student
            </button>
            <button 
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </div>
      ), {
        duration: 10000,
      });
      return;
    }

    // Regular update if not detained
    const loadingToast = toast.loading("Updating Student...");
    
    try {
      const formData = new FormData();
      
      // Append all fields with proper handling - allow empty strings for optional fields
      formData.append("enrollmentNo", data.enrollmentNo || "");
      formData.append("firstName", data.firstName || "");
      formData.append("middleName", data.middleName || "");
      formData.append("lastName", data.lastName || "");
      formData.append("email", data.email || "");
      formData.append("phoneNumber", data.phoneNumber || "");
      formData.append("semester", data.semester || "");
      formData.append("branch", data.branch || "");
      formData.append("batch", data.batch || "");
      formData.append("regulation", data.regulation || "");
      formData.append("gender", data.gender || "");
      
      // Parent fields - can be empty
      formData.append("FatherName", data.FatherName || "");
      formData.append("MotherName", data.MotherName || "");
      formData.append("FatherPhoneNumber", data.FatherPhoneNumber || "");
      formData.append("MotherPhoneNumber", data.MotherPhoneNumber || "");
      
      // Boolean fields
      formData.append("detained", data.detained ? "true" : "false");
      formData.append("passed", data.passed ? "true" : "false");
      
      if (file) {
        formData.append("type", "profile");
        formData.append("profile", file);
      }
      
      console.log("Sending update for ID:", id);
      
      const response = await axios.put(
        `${baseApiURL()}/student/details/updateDetails/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.dismiss(loadingToast);
      
      if (response.data.success) {
        toast.success(response.data.message || "Student updated successfully!");
        clearSearchHandler();
      } else {
        toast.error(response.data.message || "Failed to update student");
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Update error:", error);
      
      // Enhanced error handling
      if (error.response) {
        toast.error(error.response.data?.message || `Server error: ${error.response.status}`);
        console.log("Server response:", error.response.data);
      } else if (error.request) {
        toast.error("No response from server. Please check your connection.");
      } else {
        toast.error("Error: " + error.message);
      }
    }
  };

  const searchStudentHandler = (e) => {
    e.preventDefault();
    
    if (!search || search.trim() === "") {
      toast.error("Please enter an enrollment number");
      return;
    }

    setSearchActive(true);
    const loadingToast = toast.loading("Getting Student...");
    
    const headers = {
      "Content-Type": "application/json",
    };
    
    axios
      .post(
        `${baseApiURL()}/student/details/getDetails`,
        { enrollmentNo: search.trim() },
        { headers }
      )
      .then((response) => {
        toast.dismiss(loadingToast);
        
        if (response.data.success) {
          if (!response.data.user || response.data.user.length === 0) {
            toast.error("No Student Found!");
            setSearchActive(false);
          } else {
            const student = response.data.user[0];
            toast.success(response.data.message || "Student found!");
            
            setData({
              enrollmentNo: student.enrollmentNo || "",
              firstName: student.firstName || "",
              middleName: student.middleName || "",
              lastName: student.lastName || "",
              email: student.email || "",
              phoneNumber: student.phoneNumber || "",
              FatherName: student.FatherName || "",
              MotherName: student.MotherName || "",
              FatherPhoneNumber: student.FatherPhoneNumber || "",
              MotherPhoneNumber: student.MotherPhoneNumber || "",
              semester: student.semester ? student.semester.toString() : "",
              branch: student.branch || "",
              batch: student.batch ? student.batch.toString() : "",
              regulation: student.regulation || "",
              gender: student.gender || "",
              profile: student.profile || "",
              detained: student.detained || false,
              passed: student.passed || false,
            });
            setId(student._id);
            setPreviewImage("");
          }
        } else {
          toast.error(response.data?.message || "Failed to fetch student details");
          setSearchActive(false);
        }
      })
      .catch((error) => {
        toast.dismiss(loadingToast);
        console.error("Search error:", error);
        
        if (error.response) {
          toast.error(error.response.data?.message || `Error: ${error.response.status}`);
        } else if (error.request) {
          toast.error("No response from server. Please check your connection.");
        } else {
          toast.error("Error searching for student: " + error.message);
        }
        setSearchActive(false);
      });
  };

  const clearSearchHandler = () => {
    setSearchActive(false);
    setSearch("");
    setId("");
    setPreviewImage("");
    setFile(undefined);
    setData({
      enrollmentNo: "",
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      FatherName: "",
      MotherName: "",
      FatherPhoneNumber: "",
      MotherPhoneNumber: "",
      semester: "",
      branch: "",
      batch: "",
      regulation: "",
      gender: "",
      profile: "",
      detained: false,
      passed: false,
    });
  };

  return (
    <div className="w-full space-y-6">
      {/* Search Bar Container */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs max-w-xl mx-auto">
        <form
          className="flex items-center gap-2"
          onSubmit={searchStudentHandler}
        >
          <div className="relative flex-1">
            <input
              type="text"
              className="w-full pl-4 pr-10 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 font-mono"
              placeholder="Enter Student Enrollment No..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {!searchActive ? (
            <button className="p-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer flex items-center gap-1.5" type="submit">
              <FiSearch className="text-sm" />
              <span>Search</span>
            </button>
          ) : (
            <button
              className="p-2.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5"
              onClick={clearSearchHandler}
              type="button"
            >
              <FiX className="text-sm" />
              <span>Clear</span>
            </button>
          )}
        </form>
      </div>
      
      {searchActive && id && (
        <form
          onSubmit={updateStudentProfile}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs max-w-4xl mx-auto space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="batch" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Batch Year
              </label>
              <select
                id="batch"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                value={data.batch}
                onChange={(e) => setData({ ...data, batch: e.target.value })}
              >
                <option value="">-- Select --</option>
                {Array.from({ length: 8 }).map((_, idx) => {
                  const year = new Date().getFullYear() - idx;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            
            <div>
              <label htmlFor="firstname" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                First Name *
              </label>
              <input
                type="text"
                id="firstname"
                required
                value={data.firstName}
                onChange={(e) => setData({ ...data, firstName: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label htmlFor="middlename" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Middle Name
              </label>
              <input
                type="text"
                id="middlename"
                value={data.middleName}
                onChange={(e) => setData({ ...data, middleName: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label htmlFor="lastname" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                id="lastname"
                required
                value={data.lastName}
                onChange={(e) => setData({ ...data, lastName: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label htmlFor="enrollmentNo" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Enrollment No *
              </label>
              <input
                disabled
                type="text"
                id="enrollmentNo"
                value={data.enrollmentNo}
                className="w-full px-3.5 py-2.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 rounded-xl text-xs font-mono cursor-not-allowed"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={data.email}
                onChange={(e) => setData({ ...data, email: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter email address"
              />
            </div>
            
            <div>
              <label htmlFor="phoneNumber" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Phone Number *
              </label>
              <input
                type="text"
                id="phoneNumber"
                required
                value={data.phoneNumber}
                onChange={(e) =>
                  setData({ ...data, phoneNumber: e.target.value })
                }
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label htmlFor="FatherName" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Father Name (Optional)
              </label>
              <input
                type="text"
                id="FatherName"
                value={data.FatherName}
                onChange={(e) => setData({ ...data, FatherName: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter father's name"
              />
            </div>
            
            <div>
              <label htmlFor="MotherName" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Mother Name (Optional)
              </label>
              <input
                type="text"
                id="MotherName"
                value={data.MotherName}
                onChange={(e) => setData({ ...data, MotherName: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter mother's name"
              />
            </div>
            
            <div>
              <label htmlFor="FatherPhoneNumber" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Father Phone (Optional)
              </label>
              <input
                type="text"
                id="FatherPhoneNumber"
                value={data.FatherPhoneNumber}
                onChange={(e) => setData({ ...data, FatherPhoneNumber: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter father's phone"
              />
            </div>
            
            <div>
              <label htmlFor="MotherPhoneNumber" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Mother Phone (Optional)
              </label>
              <input
                type="text"
                id="MotherPhoneNumber"
                value={data.MotherPhoneNumber}
                onChange={(e) => setData({ ...data, MotherPhoneNumber: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter mother's phone"
              />
            </div>
            
            <div>
              <label htmlFor="semester" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Semester
              </label>
              <select
                id="semester"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                value={data.semester}
                onChange={(e) => setData({ ...data, semester: e.target.value })}
              >
                <option value="">-- Select --</option>
                <option value="1">1st Semester</option>
                <option value="2">2nd Semester</option>
                <option value="3">3rd Semester</option>
                <option value="4">4th Semester</option>
                <option value="5">5th Semester</option>
                <option value="6">6th Semester</option>
                <option value="7">7th Semester</option>
                <option value="8">8th Semester</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="branch" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Branch
              </label>
              <select
                id="branch"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                value={data.branch}
                onChange={(e) => setData({ ...data, branch: e.target.value })}
              >
                <option value="">-- Select --</option>
                {branch?.map((b) => (
                  <option value={b.name} key={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="regulation" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Regulation
              </label>
              <input
                type="text"
                id="regulation"
                required
                value={data.regulation}
                onChange={(e) => setData({ ...data, regulation: e.target.value.toUpperCase() })}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 font-bold"
                placeholder="e.g. R20"
              />
            </div>
            
            <div>
              <label htmlFor="gender" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Gender
              </label>
              <select
                id="gender"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
                value={data.gender}
                onChange={(e) => setData({ ...data, gender: e.target.value })}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            
            {/* Checkboxes section */}
            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center space-x-2 text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  id="detained"
                  checked={data.detained}
                  onChange={(e) => setData({ ...data, detained: e.target.checked })}
                  className="rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Detained</span>
              </label>
              <label className="flex items-center space-x-2 text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  id="passed"
                  checked={data.passed}
                  onChange={(e) => setData({ ...data, passed: e.target.checked })}
                  className="rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Passed</span>
              </label>
            </div>

            <div>
              <label htmlFor="file" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Select New Profile
              </label>
              <label
                htmlFor="file"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 flex justify-center items-center cursor-pointer transition"
              >
                Upload
                <span className="ml-2">
                  <FiUpload />
                </span>
              </label>
              <input
                hidden
                type="file"
                id="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>
          </div>
          
          {(previewImage || data.profile) && (
            <div className="w-full flex justify-center items-center py-2">
              <img 
                src={previewImage || getFileUrl(data.profile)} 
                alt="student" 
                className="h-32 rounded-2xl border-2 border-indigo-100 dark:border-indigo-800 shadow-md object-cover"
              />
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <button
              type="submit"
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-xs transition cursor-pointer"
            >
              Update Student Profile
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditStudent;